import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Sectors from "@/pages/Sectors";
import Customers from "@/pages/Customers";
import WhatsAppSimulator from "@/pages/WhatsAppSimulator";
import Tenants from "@/pages/Tenants";
import UsersPage from "@/pages/Users";
import Queues from "@/pages/Queues";
import OsTypes from "@/pages/OsTypes";
import ChatFlows from "@/pages/ChatFlows";
import Branding from "@/pages/Branding";
import AuditLog from "@/pages/AuditLog";
import NotificationSettings from "@/pages/NotificationSettings";
import OsStatuses from "@/pages/OsStatuses";
import Reports from "@/pages/Reports";
import Channels from "@/pages/Channels";

function Protected({ children }) {
  const { user } = useAuth();
  if (!user) {
    const here = window.location.pathname + window.location.search;
    if (here !== "/" && !here.startsWith("/login")) sessionStorage.setItem("os_redirect", here);
    return <Navigate to="/login" replace />;
  }
  return children;
}

function Home() {
  const { isSuper, activeTenant } = useAuth();
  if (isSuper && !activeTenant) return <Navigate to="/tenants" replace />;
  return <Dashboard />;
}

function NoConsulta({ children }) {
  const { user } = useAuth();
  return user?.role === "CONSULTA" ? <Navigate to="/" replace /> : children;
}

function Guard({ perm, admin, children }) {
  const { hasPerm, activeTenant, isSuper, isAdmin } = useAuth();
  if (!activeTenant) return <Navigate to={isSuper ? "/tenants" : "/login"} replace />;
  if (admin && !isAdmin) return <Navigate to="/" replace />;
  if (perm && !hasPerm(...perm)) return <Navigate to="/" replace />;
  return children;
}

function SuperOnly({ children }) {
  const { isSuper } = useAuth();
  return isSuper ? children : <Navigate to="/" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/whatsapp" element={<Protected><NoConsulta><WhatsAppSimulator /></NoConsulta></Protected>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Home />} />
            <Route path="/tenants" element={<SuperOnly><Tenants /></SuperOnly>} />
            <Route path="/orders" element={<Guard perm={["view_os", "list_os"]}><Orders /></Guard>} />
            <Route path="/orders/:id" element={<Guard perm={["view_os"]}><OrderDetail /></Guard>} />
            <Route path="/customers" element={<Guard perm={["view_os"]}><Customers /></Guard>} />
            <Route path="/reports" element={<Guard perm={["reports"]}><Reports /></Guard>} />
            <Route path="/users" element={<Guard perm={["manage_users"]}><UsersPage /></Guard>} />
            <Route path="/queues" element={<Guard perm={["manage_queues"]}><Queues /></Guard>} />
            <Route path="/sectors" element={<Guard perm={["manage_settings", "manage_queues"]}><Sectors /></Guard>} />
            <Route path="/os-types" element={<Guard perm={["manage_settings", "manage_queues"]}><OsTypes /></Guard>} />
            <Route path="/channels" element={<Guard perm={["manage_channels"]}><Channels /></Guard>} />
            <Route path="/chat-flows" element={<Guard perm={["manage_queues"]}><ChatFlows /></Guard>} />
            <Route path="/os-statuses" element={<Guard perm={["manage_queues", "manage_settings"]}><OsStatuses /></Guard>} />
            <Route path="/branding" element={<SuperOnly><Branding /></SuperOnly>} />
            <Route path="/audit" element={<Guard admin><AuditLog /></Guard>} />
            <Route path="/notifications" element={<NoConsulta><NotificationSettings /></NoConsulta>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
